import { AddCircle, ArrowDownward, ArrowUpward, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  List,
  ListItemButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { TitleResponse } from 'src/modules/member/models/titleResponse';
import { TitleUserFlagEnum } from 'src/shared/models/enums/titleUserFlag.enum';
import { OrganizationTitleTypeEnum } from 'src/shared/models/enums/organizationTitleType.enum';
import { createTitle, deleteTitle, getAllTitles, updateTitle, TitleRequest } from 'src/shared/services/titleService';

// Uppdragen delas i två typer, som i FS 1.0. Fler går inte att skapa.
const types = [OrganizationTitleTypeEnum.Styrelse, OrganizationTitleTypeEnum.Förtroendevalda];

// Samma tre nivåer som profilgrupperna, och samma ordning i rutan. shortLabel används i
// sammanfattningen under kryssrutorna.
const flagOptions = [
  { label: 'memberProfile:createProfile.userOrganization', shortLabel: 'settings:titles.levelOrganization', value: TitleUserFlagEnum.Organization },
  { label: 'memberProfile:createProfile.userDistrict', shortLabel: 'settings:titles.levelDistrict', value: TitleUserFlagEnum.District },
  { label: 'memberProfile:createProfile.userAssociation', shortLabel: 'settings:titles.levelAssociation', value: TitleUserFlagEnum.Association },
];

type TitleForm = {
  title: string;
  required: boolean;
  userFlags: number;
};

export const MemberRegistryTitlesPage = () => {
  const { t } = useTranslation(['settings', 'memberProfile', 'member', 'common']);
  const isMounted = useIsMounted();

  const [titles, setTitles] = useState<TitleResponse[]>([]);
  const [selected, setSelected] = useState<TitleResponse | undefined>(undefined);
  const [newType, setNewType] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  const formMethods = useForm<TitleForm>({
    defaultValues: { title: '', required: false, userFlags: TitleUserFlagEnum.All },
  });

  const { reset, watch, setValue, getValues } = formMethods;

  // Visar i klartext vad markeringen innebär. Utan markering gäller uppdraget på alla nivåer,
  // samma som när alla tre är markerade.
  const selectedFlags = watch('userFlags') || 0;
  // eslint-disable-next-line no-bitwise
  const selectedLevels = flagOptions.filter((x) => (selectedFlags & x.value) !== 0);

  const availableForSummary =
    selectedLevels.length === 0 || selectedLevels.length === flagOptions.length
      ? t('settings:titles.availableForAll')
      : t('settings:titles.availableForSome', {
          levels: selectedLevels
            .map((x) => t(x.shortLabel))
            .reduce((text, level, index, list) => (index === 0 ? level : `${text}${index === list.length - 1 ? ' och ' : ', '}${level}`), ''),
        });

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      // Inställningsvyn visar alla uppdrag, även de som hör till en annan nivå än användarens.
      const result = await getAllTitles(null);

      if (isMounted()) setTitles(result.data.results.slice().sort((a, b) => a.type - b.type || a.position - b.position));
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const select = (item: TitleResponse) => {
    setNewType(undefined);
    setSelected(item);
    reset({ title: item.title, required: item.required, userFlags: item.userFlags || TitleUserFlagEnum.All });
  };

  const startNew = (type: number) => {
    setSelected(undefined);
    setNewType(type);
    reset({ title: '', required: false, userFlags: TitleUserFlagEnum.All });
  };

  const save = async () => {
    const data = getValues();

    if (!data.title.trim()) return;

    const type = selected ? selected.type : newType ?? OrganizationTitleTypeEnum.Styrelse;
    const inType = titles.filter((x) => x.type === type);

    const request: TitleRequest = {
      title_id: selected?.title_id,
      title: data.title.trim(),
      required: data.required,
      // Utan markering sparas inget värde, vilket betyder tillgängligt för alla nivåer.
      userFlags: data.userFlags || null,
      type,
      position: selected ? selected.position : inType.length + 1,
    };

    try {
      if (selected) await updateTitle(request);
      else await createTitle(request);

      if (isMounted()) {
        toast.success(t('common:success.save') as string);
        setSelected(undefined);
        setNewType(undefined);
        fetchData();
      }
    } catch (e) {
      ConsoleHelper.log(e);
    }
  };

  const remove = async () => {
    if (!selected) return;

    try {
      await deleteTitle(selected.title_id);

      if (isMounted()) {
        toast.success(t('common:success.delete') as string);
        setSelected(undefined);
        fetchData();
      }
    } catch (e) {
      // Uppdrag som används av medlemmar kan inte raderas. Servern svarar med ett meddelande som
      // interceptorn visar.
      ConsoleHelper.log(e);
    }
  };

  // Ordningen byts genom att de två uppdragen byter position med varandra.
  const move = async (item: TitleResponse, direction: -1 | 1) => {
    const inType = titles.filter((x) => x.type === item.type);
    const index = inType.findIndex((x) => x.title_id === item.title_id);
    const other = inType[index + direction];

    if (!other) return;

    try {
      await updateTitle({ ...item, position: other.position });
      await updateTitle({ ...other, position: item.position });

      fetchData();
    } catch (e) {
      ConsoleHelper.log(e);
    }
  };

  const typeHeading = (type: number) =>
    type === OrganizationTitleTypeEnum.Styrelse ? t('member:memberAdvancedSearch.boardTitle') : t('member:memberAdvancedSearch.electedTitle');

  const editing = !!selected || newType !== undefined;

  return (
    <FormLoaderWrapper loading={loading}>
      <>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              {types.map((type) => (
                <Box key={type} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant='body1' sx={{ fontWeight: 600 }}>
                      {typeHeading(type)}
                    </Typography>
                    <Button size='small' endIcon={<AddCircle fontSize='small' />} onClick={() => startNew(type)}>
                      {t('settings:titles.add')}
                    </Button>
                  </Box>
                  <Divider />
                  <List dense>
                    {titles
                      .filter((x) => x.type === type)
                      .map((item, index, list) => (
                        <ListItemButton
                          key={item.title_id}
                          selected={selected?.title_id === item.title_id}
                          onClick={() => select(item)}
                          sx={{ pr: 0 }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>{item.title}</Typography>
                          <IconButton
                            size='small'
                            disabled={index === 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              move(item, -1);
                            }}
                          >
                            <ArrowUpward fontSize='small' />
                          </IconButton>
                          <IconButton
                            size='small'
                            disabled={index === list.length - 1}
                            onClick={(e) => {
                              e.stopPropagation();
                              move(item, 1);
                            }}
                          >
                            <ArrowDownward fontSize='small' />
                          </IconButton>
                        </ListItemButton>
                      ))}
                    {titles.filter((x) => x.type === type).length === 0 && (
                      <Typography variant='body2' color='text.secondary' sx={{ p: 1 }}>
                        {t('settings:titles.none')}
                      </Typography>
                    )}
                  </List>
                </Box>
              ))}
            </Grid>

            <Grid item xs={12} lg={6}>
              {editing && (
                <FormProvider {...formMethods}>
                  <Typography variant='body1' sx={{ fontWeight: 600, mb: 2 }}>
                    {selected ? t('settings:titles.edit') : t('settings:titles.add')}
                  </Typography>

                  <ControlledTextField name='title' label={t('settings:titles.name')} fullWidth />

                  <FormGroup sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={<Checkbox checked={!!watch('required')} onChange={(e) => setValue('required', e.target.checked)} />}
                      label={t('settings:titles.required')}
                    />
                  </FormGroup>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
                    <FormLabel>{t('settings:titles.availableFor')}</FormLabel>
                    <Tooltip title={t('settings:titles.availableForHelp')} arrow>
                      <InfoOutlined fontSize='small' sx={{ color: 'text.disabled', cursor: 'default', opacity: 0.9 }} />
                    </Tooltip>
                  </Box>
                  <FormGroup sx={{ ml: 1 }}>
                    {flagOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            // eslint-disable-next-line no-bitwise
                            checked={(watch('userFlags') & option.value) !== 0}
                            onChange={(e) => {
                              const current = watch('userFlags') || 0;
                              // eslint-disable-next-line no-bitwise
                              setValue('userFlags', e.target.checked ? current | option.value : current & ~option.value);
                            }}
                          />
                        }
                        label={t(option.label)}
                      />
                    ))}
                  </FormGroup>

                  <Typography variant='body2' color='text.secondary' sx={{ ml: 1, mt: 0.5, maxWidth: '32rem' }}>
                    {availableForSummary}
                  </Typography>

                  <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                    <Button variant='contained' disableElevation onClick={save}>
                      {t('common:button.save')}
                    </Button>
                    <Button
                      variant='outlined'
                      onClick={() => {
                        setSelected(undefined);
                        setNewType(undefined);
                      }}
                    >
                      {t('common:button.cancel')}
                    </Button>
                    {selected && (
                      <Button variant='outlined' color='error' sx={{ ml: 'auto' }} onClick={() => setDeleteOpen(true)}>
                        {t('common:button.delete')}
                      </Button>
                    )}
                  </Box>
                </FormProvider>
              )}
            </Grid>
          </Grid>
        </Box>

        <DeleteConfirmationDialog
          open={deleteOpen}
          value=''
          title={t('common:confirmDelete')}
          modaltext={t('settings:titles.deleteConfirm', { title: selected?.title })}
          confirmButtonColor='error'
          onClose={(value) => {
            setDeleteOpen(false);

            if (value) remove();
          }}
        />
      </>
    </FormLoaderWrapper>
  );
};
